/* 
-------

DOCS | STATE MESSAGE FACTORY:

-------

INTRODUCTION

The set of components StateMessageFactory and StateMessage
follow the FACTORY DESIGN PATTERN.
(Visit for more info: https://refactoring.guru/design-patterns/factory-method). 

The function StateMessageFactory is in charge of generating the 
StateMessage components you need based on the type of message you
pass to the function as parameter. 

This way we centralize the creation of this types of components and their
data in one place. It's easier to mantain and easier to generate
new types of StateMessages in the future.

-------

UPDATE FACTORY INSTRUCTIONS:

1. Contruct text data based on i18n keys. 
Use the state-messages object in the locale json file.

2. For images import the asset in this factory file 
and pass it as a prop of the StateMessage component

3. For btn icons follow the same instructions as for images

-------

USE INSTRUCTIONS:

Call the function { StateMessageFactory(type: StateMessageType) }
anywhere you wish to create a new state message. 

Available types can be found in the file
"/src/components/shared/state-messages/types.ts"

*/

import type { IStateItemProps, StateMessageType } from "./types";
import { StateMessage } from "./StateMessage";

// Images
import noSearchResultsImg from "@/assets/state-messages/no-search-results.svg";
import resetPasswordSuccessImg from "@/assets/state-messages/reset-password-success.svg";
import genericErrorImg from "@/assets/state-messages/generic-error.svg";
import noDataWeeklyImg from "@/assets/state-messages/no-data-weekly.svg";
import noDataYetImg from "@/assets/state-messages/no-data-yet.svg";
import emptyNotificationsImg from "@/assets/state-messages/empty-notifications.svg";
import changePasswordImg from "@/assets/reset-password/new-password.svg";

// Icons
import { ArrowRight, Send } from "lucide-react";

const messageData: Record<StateMessageType, IStateItemProps> = {
  "unable-to-load": {
    title: "state_messages.unable_to_load.title",
    subtitle: "state_messages.unable_to_load.subtitle",
    img: noDataYetImg,
  },
  "no-treatment-reviews": {
    title: "state_messages.no_treatment_reviews.title",
    subtitle: "state_messages.no_treatment_reviews.subtitle",
    img: noSearchResultsImg,
  },
  "no-search-results": {
    title: "state_messages.no_search_results.title",
    subtitle: "state_messages.no_search_results.subtitle",
    img: noSearchResultsImg,
  },
  "reset-password-success": {
    title: "state_messages.reset_password_success.title",
    subtitle: "state_messages.reset_password_success.subtitle",
    img: resetPasswordSuccessImg,
    btn: "state_messages.reset_password_success.btn",
    btnIcon: <ArrowRight />,
    route: "/login",
  },
  "generic-error": {
    title: "state_messages.generic_error.title",
    subtitle: "state_messages.generic_error.subtitle",
    img: genericErrorImg,
    btn: "state_messages.generic_error.btn",
    route: "/dashboard",
    btnIcon: <ArrowRight />,
  },
  "no-data-weekly": {
    title: "state_messages.no_data_weekly.title",
    subtitle: "state_messages.generic_error.subtitle",
    img: noDataWeeklyImg,
  },
  "no-data-yet": {
    title: "state_messages.no_data_yet.title",
    subtitle: "state_messages.no_data_yet.subtitle",
    img: noDataYetImg,
  },
  "empty-notifications": {
    title: "state_messages.empty_notifications.title",
    subtitle: "state_messages.empty_notifications.subtitle",
    img: emptyNotificationsImg,
  },
  "change-password-success": {
    title: "state_messages.change_password.title",
    subtitle: "state_messages.change_password.subtitle",
    img: changePasswordImg,
    btn: "state_messages.reset_password_success.btn",
    btnIcon: <ArrowRight />,
    route: "/login",
  },
  "invitation-sent": {
    title: "state_messages.invitation_sent.title",
    subtitle: "state_messages.invitation_sent.subtitle",
    img: resetPasswordSuccessImg,
    btn: "state_messages.invitation_sent.btn",
    route: "/dashboard/patients/invites",
    btnIcon: <Send />,
  },
  "invitation-duplicated": {
    title: "state_messages.invitation_duplicated.title",
    subtitle: "state_messages.invitation_duplicated.subtitle",
    img: noDataYetImg,
    btn: "state_messages.invitation_duplicated.btn",
    route: "/dashboard/patients/invites",
    btnIcon: <Send />,
  },
  "invitation-generic-error": {
    title: "state_messages.invitation_generic_error.title",
    subtitle: "state_messages.invitation_generic_error.subtitle",
    img: genericErrorImg,
    btn: "state_messages.invitation_generic_error.btn",
    route: "/dashboard/patients/invites",
  },
  "no-invitations-yet": {
    title: "state_messages.no_invitations_yet.title",
    subtitle: "state_messages.no_invitations_yet.subtitle",
    img: emptyNotificationsImg,
    btn: "state_messages.no_invitations_yet.btn",
    route: "/dashboard/patients/new",
  },
  "no-patients-yet": {
    title: "state_messages.no_patients_yet.title",
    subtitle: "state_messages.no_patients_yet.subtitle",
    img: emptyNotificationsImg,
    btn: "state_messages.no_patients_yet.btn",
    route: "/dashboard/patients/new",
  },
  "patient-not-found": {
    title: "state_messages.patient_not_found.title",
    subtitle: "state_messages.patient_not_found.subtitle",
    img: noDataYetImg,
    btn: "state_messages.patient_not_found.btn",
    route: "/dashboard/patients",
  },
  "no-active-prescriptions": {
    title: "state_messages.no_active_prescriptions.title",
    subtitle: "state_messages.no_active_prescriptions.subtitle",
    img: noSearchResultsImg,
    btn: "state_messages.no_active_prescriptions.btn",
    route: "/dashboard/medications",
  },
};

export const StateMessageFactory = (type: StateMessageType) => {
  const messageProps = messageData[type];
  return <StateMessage {...messageProps} />;
};
